"use client";
import React, { useState, useEffect, useCallback } from "react";
import { CameraIcon } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { FaTimes } from "react-icons/fa";
import { MdArrowForwardIos } from "react-icons/md";
import { SliderImagesProps } from "../../design3/components/PropertyCarousel2";
import VideoPlayer from "../../design1/components/VideoComponent";
// type PropType = {
//   slides: SliderImagesProps[];
//   options?: EmblaOptionsType;
//   url?: string;
//   variant?: boolean;
// };

// function cn(...classes: (string | boolean)[]) {
//   return classes.filter(Boolean).join(" ");
// }

interface ImageGridProps {
  images: SliderImagesProps[];
}

const ImageGrid: React.FC<ImageGridProps> = ({ images }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const slides = images;
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel({ loop: true });


  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi) return;
      setOpen(true);
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi) return;

    setSelectedIndex(emblaMainApi.selectedScrollSnap());
 
  }, [emblaMainApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();

    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  const [open, setOpen] = useState(false);
  // const visibleImages = images.slice(0, 4);
  // const moreImagesCount = images.length - visibleImages.length;

  return (
    <>
      <div
        onClick={() => {
          if (open) {
            setOpen(false);
          }
        }}
        className={`${
          open
            ? " inset-0 z-[1000] fixed h-screen w-screen bg-black/90"
            : "h-0 overflow-hidden"
        }`}
      >
        {open && (
          <>
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (emblaMainApi) {
                  emblaMainApi.scrollPrev();
                }
              }}
              className="prev top-1/2 left-4 absolute z-[200]  -translate-y-1/2 transform rotate-[180deg]"
            >
              {<MdArrowForwardIos size={48} color="white" />}
            </div>
            <div
              onClick={(e) => {
                e.stopPropagation();
                if (emblaMainApi) {
                  emblaMainApi.scrollNext();
                }
              }}
              className="next top-1/2 right-4 absolute z-[200]  -translate-y-1/2"
            >
              <MdArrowForwardIos size={48} color="white" />
            </div>
          </>
        )}

        {open && (
          <div className="absolute flex gap-1 left-1/2 -translate-x-1/2 top-4 z-[10] w-max  items-end text-white  bg-themeColor rounded-md p-2 ">
            <CameraIcon />
            <span>
              <span>
                {selectedIndex + 1} / {slides.length + 1}
              </span>
            </span>
          </div>
        )}

        <span
          onClick={() => {
            setOpen(false);
          }}
          className={`absolute cursor-pointer  top-5 left-4 ${
            open ? "block" : "hidden"
          } text-xl text-white`}
        >
          <span className="cursor-pointer">
            <FaTimes />
          </span>
        </span>
        <div
          className={`embla__viewport ${
            open && "h-full w-full"
          }  rounded-xl overflow-hidden`}
          ref={emblaMainRef}
        >
          <div
            className={`flex  ${open ? "h-full" : "xs:h-[400px] h-[500px]"} `}
          >
            {slides.map((item, index) => (
              <div
                onClick={() => {
                  setOpen(true);
                }}
                className="embla__slide flex justify-center h-full basis-full shrink-0 "
                key={index}
              >
                {item.type === "image" ? (
                  <img
                    onClick={(e) => {
                      if (open) {
                        e.stopPropagation();
                      }
               
                    }}
                    className={`object-cover${
                      open &&
                      "h-auto sm:max-h-[60vh] object-cover max-h-[70vh] block my-auto mx-auto max-w-[90vw] w-auto"
                    } h-full w-full`}
                    src={item.url}
                    alt=""
                  />
                ) : (
                  <VideoPlayer url={item.url} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <ImageGrid2 onThumbClick={onThumbClick} images={images} />
    </>
  );
};

export default ImageGrid;

export const ImageOrVideo: React.FC<SliderImagesProps> = ({ type, url }) => {
  if (type === "image") {
    return <img className="h-full w-full object-cover" src={url} alt="" />;
  }
  return (
    <div className="h-full w-full">
      <VideoPlayer className="!h-full" url={url} />
    </div>
  );
};
// Adjust import based on your project

export const ImageGrid2 = ({
  images,
  onThumbClick,
}: {
  images: SliderImagesProps[];
  onThumbClick: (index: number) => void;
}) => {
  const visibleImages = images.slice(0, 4);
  const moreImagesCount = images.length - visibleImages.length;

  return (
    <div
      className={`grid gap-2 w-[100%] max-h-[600px] overflow-hidden  min-h-[250px] h-[100%] ${getGridClass(
        images.length
      )}`}
    >
      {visibleImages.map((image, index) => (
        <div
          key={index}
          onClick={() => onThumbClick(index)}
          className={getImageClass(index, images.length)}
        >
          <ImageOrVideo
            {...image}
            // className="w-full relative h-full object-cover rounded-lg"
          />
          {index === 3 && moreImagesCount > 0 && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white text-xl font-bold">
              +{images.length - 4} more
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Function to return grid structure based on image count
const getGridClass = (count: number) => {
  if (count === 1) return "grid-cols-1 grid-rows-1"; // Single image: full size
  if (count === 2) return "grid-cols-2 grid-rows-1"; // Two images: side by side
  if (count === 3) return "grid-cols-2 grid-rows-[1fr_1fr]"; // Three images: 1/2 width each, first full height, second and third 1/2 height
  if (count >= 4) return "grid-cols-[2fr_1fr_1fr] grid-rows-[1fr_1fr]"; // Four or more images: 200px (left), 100px+100px (right), 200px rows
  return "grid-cols-2 grid-rows-2"; // Fallback (shouldn't be reached)
};

// Function to return specific image sizes and positions
const getImageClass = (index: number, total: number) => {
  const baseClass =
    "relative rounded-md overflow-hidden max-h-full max-w-full w-full h-full"; // Ensure full size within grid cell

  if (total === 1) {
    return `${baseClass} col-span-1 row-span-1`; // Full size
  }

  if (total === 2) {
    return `${baseClass} col-span-1 row-span-1`; // 1/2 width, full height each
  }

  if (total === 3) {
    if (index === 0) return `${baseClass} col-span-1 row-span-2`; // 1/2 width, full height (left)
    if (index === 1) return `${baseClass} col-span-1 row-span-1`; // 1/2 width, 1/2 height (top-right)
    if (index === 2) return `${baseClass} col-span-1 row-span-1`; // 1/2 width, 1/2 height (bottom-right)
  }

  if (total >= 4) {
    if (index === 0) return `${baseClass} col-span-1 row-span-2`; // 200x400 left
    if (index === 1)
      return `${baseClass} col-span-2 row-span-1 col-start-2 row-start-1`; // 200x200 top-right (spans both 100px columns)
    if (index === 2)
      return `${baseClass} col-span-1 row-span-1 col-start-2 row-start-2`; // 100x200 bottom-right (left half)
    if (index === 3)
      return `${baseClass} col-span-1 row-span-1 col-start-3 row-start-2`; // 100x200 bottom-right (right half)
  }

  return baseClass; // Fallback
};
